import type {
  CRMUser,
  PdfAnnotation,
  ProjectOnboarding,
  ProjectTask,
  TaskComment,
  TaskGroup,
  TimberProject,
} from '../types';
import {
  aggregateGroupComments,
  collectGroupCommentContext,
  groupHasUnread,
  latestForeignGroupComment,
} from './groupComments';
import { displayNameFromFileRef } from './projectFileRef';
import { pdfAnnotationHasUnreadForUser } from './pdfAnnotationService';
import type { ReadReceiptMap } from './taskReadReceipts';

export type ProjectHubUnreadKind = 'task' | 'pdf' | 'image' | 'csv' | 'group';

export type ProjectHubUnreadListItem = {
  id: string;
  /** Used to route the click. */
  kind: 'task' | 'file' | 'group';
  /** Used for the leading icon + the kind-prefix on the location line. */
  locationKind: ProjectHubUnreadKind;
  sortKey: number;
  /** Comment text (or group / task label for thread surfaces). */
  primary: string;
  /** Right-side hint like "Page 4" — small, optional. */
  locationDetail?: string;
  /** Clean human-readable label like "Foundation" (group) or "ANCHORITE_17.pdf" (file). */
  locationLabel: string;
  /** Display name of whoever wrote the latest unread activity. */
  author?: string;
  /** Latest unread activity timestamp (ms). */
  timestampMs: number;
  task?: ProjectTask;
  annotation?: PdfAnnotation;
  /** Consolidated group thread key (`dealId::groupname`). */
  groupKey?: string;
  /** Project to open when launching the group thread. */
  groupProjectId?: string;
};

function plainSnippet(htmlOrText: string, maxLen: number): string {
  const t = htmlOrText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return 'Comment';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

const IMAGE_EXT = /\.(png|jpe?g|webp|gif|avif|heic|bmp)(\?|$)/i;
const CSV_EXT = /\.csv(\?|$)/i;

export type AnnotationFileKind = 'pdf' | 'image' | 'csv';

export function fileKindFromAnnotation(ann: PdfAnnotation): AnnotationFileKind {
  const name = displayFileNameFromAnnotation(ann);
  const u = `${ann.documentUrl || ''} ${ann.documentId || ''}`;
  if (IMAGE_EXT.test(u) || IMAGE_EXT.test(name)) return 'image';
  if (CSV_EXT.test(name) || CSV_EXT.test(u)) return 'csv';
  return 'pdf';
}

/**
 * Best-effort clean filename for a PDF annotation. Handles:
 *   - Firebase Storage download URLs (extracts path from `o/<encoded>?…`)
 *   - Raw storage paths (e.g. `projectFiles/<projectId>/<ts>-<name>`)
 *   - Bare filenames
 * Strips a leading `<digits>-` upload-timestamp prefix and any query string.
 */
export function displayFileNameFromAnnotation(ann: PdfAnnotation): string {
  const raw = (ann.documentUrl || ann.documentId || '').trim();
  if (!raw) return 'File';
  return displayNameFromFileRef(raw);
}

function buildUserNameMap(crmUsers?: CRMUser[]): Map<string, string> {
  const m = new Map<string, string>();
  if (!crmUsers) return m;
  for (const u of crmUsers) {
    if (u.uid) m.set(u.uid, u.displayName || u.email || u.uid);
  }
  return m;
}

function annotationLatestActivity(ann: PdfAnnotation): { ms: number; author: string; text: string } {
  const rootMs = ann.createdAt instanceof Date ? ann.createdAt.getTime() : new Date(ann.createdAt).getTime();
  let bestMs = rootMs;
  let author = ann.userName || '';
  let text = ann.content || '';
  for (const r of ann.replies ?? []) {
    const t = r.createdAt instanceof Date ? r.createdAt.getTime() : new Date(r.createdAt).getTime();
    if (t > bestMs) {
      bestMs = t;
      author = r.userName || author;
      text = r.content || text;
    }
  }
  return { ms: bestMs, author, text };
}

export type BuildProjectHubUnreadInput = {
  projectId: string;
  projectTasks: ProjectTask[];
  pdfAnnotations: PdfAnnotation[];
  userId: string;
  readReceipts: ReadReceiptMap;
  crmUsers?: CRMUser[];
  taskGroups: TaskGroup[];
  taskComments: TaskComment[];
  timberProjects: TimberProject[];
  projectOnboardings: ProjectOnboarding[];
};

/**
 * Builds the list of unread items shown in the All-Projects modal:
 *   - Group threads with unread comments (read receipts keyed by groupKey)
 *   - PDF / image annotations with unread activity
 */
export function buildProjectHubUnreadListItems(input: BuildProjectHubUnreadInput): ProjectHubUnreadListItem[] {
  const {
    projectId,
    projectTasks,
    pdfAnnotations,
    userId,
    readReceipts,
    crmUsers,
    taskGroups,
    taskComments,
    timberProjects,
    projectOnboardings,
  } = input;

  if (!userId) return [];

  const userNameById = buildUserNameMap(crmUsers);
  const items: ProjectHubUnreadListItem[] = [];
  const seenGroupKeys = new Set<string>();

  const projectGroups = taskGroups.filter((g) => g.projectId === projectId && g.id);
  const currentProject = timberProjects.find((p) => p.id === projectId);

  for (const group of projectGroups) {
    const ctx = collectGroupCommentContext(
      group,
      currentProject,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
    );
    if (seenGroupKeys.has(ctx.groupKey)) continue;
    seenGroupKeys.add(ctx.groupKey);

    const comments = aggregateGroupComments(taskComments, ctx);
    if (!groupHasUnread(comments, ctx, userId, readReceipts)) continue;

    const latest = latestForeignGroupComment(comments, ctx, userId);
    if (!latest) continue;

    items.push({
      id: `group:${ctx.groupKey}`,
      kind: 'group',
      locationKind: 'group',
      sortKey: latest.ms,
      primary: plainSnippet(latest.text, 140),
      locationLabel: ctx.displayName,
      author: latest.author || undefined,
      timestampMs: latest.ms,
      groupKey: ctx.groupKey,
      groupProjectId: ctx.primaryProjectId,
    });
  }

  const taskById = new Map<string, ProjectTask>();
  for (const t of projectTasks.filter((task) => task.projectId === projectId && task.id)) {
    if (t.id) taskById.set(t.id, t);
  }

  const anns = pdfAnnotations.filter((a) => a.projectId === projectId);
  for (const ann of anns) {
    if (!ann.id || !pdfAnnotationHasUnreadForUser(ann, userId)) continue;
    const latest = annotationLatestActivity(ann);
    const locationLabel = displayFileNameFromAnnotation(ann);
    const locationKind: ProjectHubUnreadKind = fileKindFromAnnotation(ann);
    const linked = ann.taskId ? taskById.get(ann.taskId) : undefined;
    items.push({
      id: `file:${ann.id}`,
      kind: 'file',
      locationKind,
      sortKey: latest.ms,
      primary: plainSnippet(latest.text || ann.content || '', 140),
      locationLabel: linked?.title ? `${locationLabel} · ${linked.title.trim()}` : locationLabel,
      locationDetail: ann.pageNumber ? `Page ${ann.pageNumber}` : undefined,
      author: latest.author || ann.userName,
      timestampMs: latest.ms,
      annotation: ann,
    });
  }

  return items.sort((a, b) => b.sortKey - a.sortKey);
}
